import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { Section } from "@/pages/Shell";
import AdminShell from "@/pages/admin/AdminShell";
import {
  LEAD_FOCUS_COMPOSE,
  LEAD_SECTION_CONVERSATION_ID,
  LEAD_SECTION_TEMPLATES_ID,
  parseLeadFocus,
  resolveLeadFocusTarget,
  whatsappWindowOpen,
} from "@/lib/leadDetailAnchors";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { filterAssignableUsers } from "@/lib/leadAssignees";
import { normalizeStatus } from "@/lib/leadStatuses";
import LeadChecklist from "@/components/leads/LeadChecklist";
import LeadTemplates from "@/components/leads/LeadTemplates";
import LeadDossierCard from "@/components/leads/LeadDossierCard";
import LeadPreferences from "@/components/leads/LeadPreferences";
import LeadTimeline from "@/components/leads/LeadTimeline";
import LeadContactSummary from "@/components/leads/LeadContactSummary";
import LeadPipelineStepper from "@/components/leads/LeadPipelineStepper";
import LeadVillaHoldCard from "@/components/leads/LeadVillaHoldCard";
import LeadHero from "@/components/leads/LeadHero";
import LeadConversation from "@/components/leads/LeadConversation";
import LeadIntelligenceCard from "@/components/leads/LeadIntelligenceCard";
import LeadAutoReplyShadowCard from "@/components/leads/LeadAutoReplyShadowCard";
import LeadAutomationCard from "@/components/leads/LeadAutomationCard";
import {
  ArrowLeft, ChatCircleDots,
  WarningOctagon, CheckCircle, ArrowsClockwise, Copy, House, X,
  CaretDown, CaretRight,
} from "@phosphor-icons/react";

const ACCENT = "#1C1C1E";
// Deep-link reveal: cards above the anchor (e.g. Inteligencia IA) load
// asynchronously and grow AFTER the first scroll, pushing the anchor down.
// Re-align on layout growth for this long — unless the user takes over.
const FOCUS_SETTLE_MS = 2500;
const FOCUS_SETTLE_TICK_MS = 350;

const ACTION_LABEL = {
  "lead.created": "Lead creado",
  "lead.status_changed": "Cambió estado",
  "lead.assigned": "Asignó responsable",
  "lead.created_note": "Añadió nota",
  "lead.next_action_updated": "Actualizó próxima acción",
  "lead.meeting_updated": "Actualizó reunión",
  "lead.converted": "Convertido a inversor",
};

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  // Snapshot the query param at mount so the URL cleanup below (which
  // strips the value to keep it from being re-applied on re-render) does
  // not lose the initial signal. The value is forwarded to LeadTemplates
  // as `preselectedTemplateId` for one-time application.
  const initialComposeTemplateRef = useRef(searchParams.get("compose_template"));
  // Deep-link focus (see @/lib/leadDetailAnchors): "#conversation" from
  // "Abrir conversación", "#compose" (+ ?compose_template=) from
  // "Preparar mensaje". Captured once at mount — works identically for a
  // normal click and for "open in new tab".
  const initialFocusRef = useRef(parseLeadFocus({ hash: location.hash, search: location.search }));
  const focusScrolledRef = useRef(false);
  const { user: me } = useAuth();
  const [lead, setLead] = useState(null);
  const [activity, setActivity] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [villas, setVillas] = useState([]);
  const [error, setError] = useState("");
  const [newNote, setNewNote] = useState("");
  const [newNoteKind, setNewNoteKind] = useState("general");
  const [busy, setBusy] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  // Bumped by the hero "Editar contacto" quick action; LeadContactSummary
  // watches this prop and auto-toggles to edit mode on each increment.
  const [contactEditTrigger, setContactEditTrigger] = useState(0);

  // Próxima acción — local draft state. Guardar cambios se separa
  // conceptualmente de Marcar hecha: guardar aplica ediciones de
  // descripción / fecha / hora; marcar hecha (endpoint distinto)
  // registra que la acción se ejecutó. Cambio sólo de UX; endpoints y
  // payloads idénticos a los ya existentes.
  const [naDraft, setNaDraft] = useState({ desc: "", date: "", time: "" });
  const [naSavedFlash, setNaSavedFlash] = useState(false);

  const load = async () => {
    try {
      // The lead itself is the only required fetch. Supporting requests
      // (activity feed, users list for the assignee picker, villas) degrade
      // gracefully so a 403 on one of them (e.g. marketing hitting /users
      // before PR #9.1, or a temporary outage) doesn't blank the whole page.
      const l = await api.get(`/leads/${id}`);
      setLead(l.data);
      const [a, u, v] = await Promise.all([
        api.get(`/activity?entity_type=lead&entity_id=${id}&limit=200`)
          .catch(() => ({ data: [] })),
        api.get(`/users`).catch(() => ({ data: [] })),
        api.get(`/villas`).catch(() => ({ data: [] })),
      ]);
      setActivity(Array.isArray(a.data) ? a.data : []);
      const users = Array.isArray(u.data) ? u.data : [];
      setAssignees(filterAssignableUsers(users));
      setVillas(Array.isArray(v.data) ? v.data : []);
      setError("");
    } catch (e) { setError(formatError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // One-shot: clean the `compose_template` param from the URL so subsequent
  // in-page navigations (edits, note posts, etc.) don't re-trigger the
  // preselection. `initialComposeTemplateRef` keeps the value alive for
  // the render tree below.
  useEffect(() => {
    if (!searchParams.get("compose_template")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("compose_template");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Section the deep link should reveal (null when arriving without a
  // focus request). Conversations are admin-only, so for other roles —
  // or when the 24h window is closed and free text cannot be composed —
  // "compose" resolves to the "Mensajes sugeridos" card instead.
  const focusTargetId = useMemo(() => {
    if (!lead) return null;
    return resolveLeadFocusTarget({
      focus: initialFocusRef.current.focus,
      conversationMounted: me?.role === "admin",
      windowOpen: whatsappWindowOpen(lead.last_wa_inbound_at),
    });
  }, [lead, me?.role]);

  // Reveal that section ONCE the lead has loaded (never again on later
  // lead refreshes). Two rAFs so collapsibles have rendered their body.
  // Stable ids + scrollIntoView: no pixel arithmetic. Then, for a short
  // settle window, re-align whenever the document grows (async cards
  // above the anchor finishing their fetch) — stopping the moment the
  // user scrolls, touches or types.
  useEffect(() => {
    if (!focusTargetId || focusScrolledRef.current) return undefined;
    focusScrolledRef.current = true;
    const reveal = (behavior) => {
      const el = document.getElementById(focusTargetId);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior, block: "start" });
      }
    };
    let userTookOver = false;
    const takeOver = () => { userTookOver = true; };
    const passive = { passive: true };
    window.addEventListener("wheel", takeOver, passive);
    window.addEventListener("touchmove", takeOver, passive);
    window.addEventListener("keydown", takeOver);
    let raf1 = 0; let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => reveal("smooth"));
    });
    const settleUntil = Date.now() + FOCUS_SETTLE_MS;
    let lastHeight = document.body ? document.body.scrollHeight : 0;
    const settle = setInterval(() => {
      if (userTookOver || Date.now() > settleUntil) { clearInterval(settle); return; }
      const h = document.body ? document.body.scrollHeight : 0;
      if (h !== lastHeight) { lastHeight = h; reveal("auto"); }
    }, FOCUS_SETTLE_TICK_MS);
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      clearInterval(settle);
      window.removeEventListener("wheel", takeOver, passive);
      window.removeEventListener("touchmove", takeOver, passive);
      window.removeEventListener("keydown", takeOver);
    };
  }, [focusTargetId]);

  // Only ONE prepared message is visible on arrival: the composer when the
  // conversation is the target, the "Mensajes sugeridos" card otherwise.
  // The card still receives the preselection (collapsed) as the fallback
  // for the cases the composer cannot apply it — the thread's own window
  // verdict or the suggestion catalog may disagree with the page guess.
  const templatesFocused = focusTargetId === LEAD_SECTION_TEMPLATES_ID;

  const patch = async (payload) => {
    setBusy(true);
    try {
      await api.patch(`/leads/${id}`, payload);
      await load();
    } catch (e) { alert(formatError(e)); }
    finally { setBusy(false); }
  };

  const addNote = async (e) => {
    e.preventDefault();
    const text = newNote.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post(`/leads/${id}/notes`, { text, kind: newNoteKind });
      setNewNote("");
      await load();
    } catch (e) { alert(formatError(e)); }
    finally { setBusy(false); }
  };

  const lastContactDays = useMemo(() => {
    if (!activity.length) return daysSince(lead?.created_at);
    return daysSince(activity[0].timestamp);
  }, [activity, lead]);

  const nextActionLate = useMemo(() => {
    if (!lead?.next_action_date) return false;
    const d = new Date(lead.next_action_date);
    if (isNaN(d)) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return d < today;
  }, [lead]);

  const notesThread = useMemo(
    () => [...(lead?.notes_thread || [])].sort((a, b) => (b.at || "").localeCompare(a.at || "")),
    [lead]
  );

  // Sync the draft when the persisted lead changes so post-save reloads
  // and external edits (e.g. Timeline actions) refresh the form.
  useEffect(() => {
    setNaDraft({
      desc: lead?.next_action || "",
      date: lead?.next_action_date || "",
      time: lead?.next_action_time || "",
    });
  }, [lead?.next_action, lead?.next_action_date, lead?.next_action_time]);

  const naDirty = useMemo(() => (
    (lead?.next_action || "") !== naDraft.desc ||
    (lead?.next_action_date || "") !== naDraft.date ||
    (lead?.next_action_time || "") !== naDraft.time
  ), [lead, naDraft]);

  // "Guardado ✓" flash auto-dismisses after 2.5s.
  useEffect(() => {
    if (!naSavedFlash) return;
    const t = setTimeout(() => setNaSavedFlash(false), 2500);
    return () => clearTimeout(t);
  }, [naSavedFlash]);

  const backLink = me?.role === "admin" ? "/leads" : "/";

  if (error) {
    return (
      <AdminShell>
        <div className="dv-card text-center py-12" data-testid="lead-error">
          <WarningOctagon size={32} weight="duotone" className="mx-auto text-red-500 mb-2" />
          <div className="font-medium mb-2">No se pudo cargar el lead</div>
          <div className="text-stone-500 text-sm mb-4">{error}</div>
          <Link to={backLink} className="dv-btn dv-btn-primary inline-flex"><ArrowLeft size={14} /> Volver</Link>
        </div>
      </AdminShell>
    );
  }
  if (!lead) {
    return <AdminShell><div className="py-16 text-center text-stone-400">Cargando…</div></AdminShell>;
  }

  const normalizedStatus = normalizeStatus(lead.status);
  const isConverted = !!lead.converted_user_id;
  const canConvert = !isConverted && (normalizedStatus === "reserved") && ["admin", "marketing"].includes(me?.role);
  const convertedVilla = lead.converted_villa_id ? villas.find(v => v.villa_id === lead.converted_villa_id) : null;

  const markNextActionDone = async () => {
    const completed = lead.next_action;
    if (!completed) return;
    try {
      await api.post(`/leads/${lead.lead_id}/notes`, {
        text: `✓ Acción completada: ${completed}`,
      });
      await patch({ next_action: null, next_action_date: null, next_action_time: null });
    } catch (e) { alert(formatError(e)); }
  };

  // Guardar cambios de la próxima acción — envío único (no optimistic).
  // Idempotente: si el usuario pulsa sin cambios, `naDirty` deshabilita el
  // botón. Preserva la regla existente de que sin fecha no puede haber hora.
  const saveNextAction = async () => {
    if (!naDirty || busy) return;
    const desc = (naDraft.desc || "").trim();
    await patch({
      next_action: desc || null,
      next_action_date: naDraft.date || null,
      next_action_time: naDraft.date ? (naDraft.time || null) : null,
    });
    setNaSavedFlash(true);
  };

  return (
    <AdminShell>
      <LeadHero
        lead={lead}
        assignees={assignees}
        busy={busy}
        backLink={backLink}
        onPatch={patch}
        onMarkActionDone={markNextActionDone}
        onRequestEditContact={() => setContactEditTrigger((n) => n + 1)}
        onConvert={() => setShowConvert(true)}
        canConvert={canConvert}
        lastActivityDays={lastContactDays}
        nextActionLate={nextActionLate}
      />

      {/* Post-conversion banner */}
      {isConverted && (
        <div
          className="mb-6 dv-card flex items-start gap-3 border-l-4"
          style={{ borderLeftColor: "#2F3E35", background: "#F0F4EE" }}
          data-testid="converted-banner"
        >
          <CheckCircle size={22} weight="fill" style={{ color: "#2F3E35" }} className="mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium" style={{ color: "#2F3E35" }}>
              Convertido a inversor · {fmtDate(lead.converted_at)}
            </div>
            <div className="text-stone-600 mt-0.5">
              Usuario <code className="bg-white px-1.5 py-0.5 rounded border border-stone-200">{lead.converted_user_id}</code>
              {convertedVilla && <> · villa asociada: <strong>{convertedVilla.name}</strong></>}
            </div>
            <div className="text-xs text-stone-500 mt-1">
              La propiedad final (<code>owner_user_id</code>) no se asignó automáticamente. Puedes hacerlo desde <Link to="/villas" className="underline">Villas</Link>.
            </div>
          </div>
        </div>
      )}


      {/* PRINCIPAL — el workspace comercial diario (primer viewport).
          Izquierda: en qué estado está + quién es / qué quiere (una sola
          card compacta). Derecha: qué toca hacer + qué me está diciendo
          (WhatsApp grande). En mobile: Estado, Información del comprador,
          Próxima acción, WhatsApp. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          {/* 1. Estado comercial */}
          <Section title="Estado comercial" subtitle="Pipeline operativo de Dolce Villas.">
            <LeadPipelineStepper
              status={lead.status}
              onChange={(nextStatus) => patch({ status: nextStatus })}
              disabled={busy}
            />
          </Section>

          {/* 2. Información del comprador — Contacto + perfil fusionados
              en UNA superficie (una sola dv-card): contacto compacto a 2
              columnas, luego Interés / Decisión / Comunicación. La hero
              "Editar contacto" sigue anclando aquí (section-contact) y
              abriendo el modo edición vía editTrigger. Contratos PATCH
              intactos; el idioma vive sólo en Contacto y "Canal actual"
              (current_conversation_channel) deja de mostrarse: es estado
              operativo, no preferencia del comprador. */}
          <div id="section-contact" data-testid="section-preferences">
            <Section title="Información del comprador" subtitle="Contacto, interés, decisión y comunicación.">
              <div className="dv-card space-y-4" data-testid="lead-buyer-info">
                <LeadContactSummary
                  lead={lead}
                  onChanged={load}
                  testid="lead-detail-contact-summary"
                  editTrigger={contactEditTrigger}
                  embedded
                  twoColumn
                  dedupeInterest
                />
                <div className="border-t border-stone-200 dark:border-stone-700" />
                <LeadPreferences
                  lead={lead}
                  onChanged={load}
                  includeChannels
                  showLanguage={false}
                  embedded
                  title={null}
                />
              </div>
            </Section>
          </div>
        </div>

        <div className="space-y-6">
          {/* 3. Próxima acción — Guardar cambios (edición de descripción /
              fecha / hora) y Marcar hecha (acción ya realizada) quedan
              separados conceptualmente. Endpoints y payloads sin cambios. */}
          <div id="section-next-action">
            <Section title="Próxima acción" subtitle="Qué toca hacer y cuándo.">
              <div className="dv-card space-y-3">
                {nextActionLate && (
                  <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded px-3 py-2" data-testid="next-action-late">
                    <WarningOctagon size={14} weight="bold" /> Próxima acción vencida
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm">
                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-1 block">Descripción</label>
                    <textarea
                      rows={3}
                      className="dv-input w-full text-sm"
                      placeholder="Ej: Llamar el jueves a las 10:00 para confirmar visita"
                      value={naDraft.desc}
                      onChange={(e) => setNaDraft(d => ({ ...d, desc: e.target.value }))}
                      disabled={busy}
                      data-testid="lead-next-action"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-1 block">Fecha</label>
                    <input
                      type="date"
                      className="dv-input"
                      value={naDraft.date}
                      onChange={(e) => setNaDraft(d => ({
                        ...d,
                        date: e.target.value,
                        time: e.target.value ? d.time : "",
                      }))}
                      disabled={busy}
                      data-testid="lead-next-action-date"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-1 block">Hora (opcional)</label>
                    <input
                      type="time"
                      className="dv-input disabled:opacity-40"
                      value={naDraft.time}
                      onChange={(e) => setNaDraft(d => ({ ...d, time: e.target.value }))}
                      disabled={busy || !naDraft.date}
                      title={naDraft.date ? "" : "Define una fecha para añadir hora"}
                      data-testid="lead-next-action-time"
                    />
                  </div>
                </div>

                {/* Guardar cambios — visible siempre; habilitado sólo si
                    hay dirty state. "Guardado ✓" es feedback discreto que
                    se auto-oculta a los 2.5s. */}
                <div className="flex items-center justify-end gap-3 pt-1">
                  {naSavedFlash && (
                    <span
                      className="text-xs text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1"
                      data-testid="lead-next-action-saved"
                    >
                      <CheckCircle size={14} weight="bold" /> Guardado
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={saveNextAction}
                    disabled={busy || !naDirty}
                    className="dv-btn dv-btn-primary text-xs"
                    data-testid="lead-next-action-save"
                    title={naDirty ? "" : "No hay cambios que guardar"}
                  >
                    Guardar cambios
                  </button>
                </div>

                {/* Marcar hecha — acción independiente. Sólo visible si hay
                    una acción persistida (evita "completar" un draft nunca
                    guardado). Separada por divisor para diferenciarla
                    visualmente de "Guardar cambios". Se bloquea mientras
                    haya cambios sin guardar para evitar completar una
                    acción que aún no refleja lo que se ve en pantalla. */}
                {lead.next_action && (
                  <>
                    <div className="border-t border-stone-200 dark:border-stone-700 -mx-4 sm:-mx-6" />
                    <div className="flex items-center justify-between gap-3 pt-1">
                      <span className="text-xs text-stone-500 dark:text-stone-400">
                        {naDirty
                          ? "Guarda los cambios antes de marcar esta acción como hecha."
                          : "¿Ya la realizaste?"}
                      </span>
                      <button
                        type="button"
                        onClick={markNextActionDone}
                        disabled={busy || naDirty}
                        data-testid="lead-next-action-done"
                        title={naDirty ? "Guarda los cambios antes de marcar esta acción como hecha." : ""}
                        className="text-xs text-stone-700 dark:text-stone-300 hover:text-emerald-700 dark:hover:text-emerald-400 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-stone-700 dark:disabled:hover:text-stone-300"
                      >
                        <CheckCircle size={14} weight="bold" /> Marcar hecha
                      </button>
                    </div>
                  </>
                )}
              </div>
            </Section>
          </div>

          {/* Secuencia automática — CONTEXTO, no una tarea.
              Va debajo de "Próxima acción" y en su propia caja a propósito:
              un paso automático no es trabajo de nadie, y mezclarlo con la
              próxima acción del comercial borraría justo la distinción que el
              modelo mantiene (`automation_sequences` vs `next_action_*`).
              Los controles son de admin: el mismo listón que mandar un
              WhatsApp a mano. */}
          <Section
            title="Automatización"
            subtitle="Qué está enviando el sistema por su cuenta, y hasta cuándo."
          >
            <LeadAutomationCard leadId={lead.lead_id} isAdmin={me?.role === "admin"} />
          </Section>

          {/* IA comercial — una sola inferencia sobre la conversación:
              cualifica, puntúa con motivos, dice qué falta por saber y
              propone la siguiente respuesta. Solo recomienda: nunca muta
              status/next_action y nunca envía. Visible para admin y
              marketing (el backend aplica el mismo guard). */}
          <Section
            title="IA comercial"
            subtitle="Cualificación y borrador de respuesta. Solo recomienda — no modifica el CRM ni envía nada."
          >
            <LeadIntelligenceCard leadId={lead.lead_id} />
          </Section>

          {/* Auto Reply en sombra — qué habría contestado el bot y si
              estuvo bien. Mismo rol REAL que la conversación, y por la
              misma razón: esto existe para comparar el mensaje del lead
              con la respuesta del bot, y sin la conversación delante es
              media comparación. Nada de esta sección envía. */}
          {me?.role === "admin" && (
            <Section
              title="Auto Reply · Sombra"
              subtitle="Qué habría respondido el bot en cada ráfaga, y por qué. Evaluar no envía nada."
            >
              <LeadAutoReplyShadowCard leadId={lead.lead_id} />
            </Section>
          )}

          {/* Conversación WhatsApp — superficie operativa primaria, grande,
              justo bajo Próxima acción. Gated on the REAL role, not
              effectiveRole: conversations are admin-only in V1 and the
              backend enforces the same guard. `lead` alimenta el picker de
              mensajes sugeridos (variables {nombre}, {villa}, …). */}
          {me?.role === "admin" && (
            <div
              id={LEAD_SECTION_CONVERSATION_ID}
              className="scroll-mt-24"
              data-testid="section-conversation"
              data-focused={focusTargetId === LEAD_SECTION_CONVERSATION_ID ? "true" : undefined}
            >
              <Section title="Conversación" subtitle="Mensajes de WhatsApp del lead.">
                <LeadConversation
                  leadId={lead.lead_id}
                  lead={lead}
                  unreadCount={lead.wa_unread_count || 0}
                  lastInboundAt={lead.last_wa_inbound_at || null}
                  preselectedTemplateId={
                    initialFocusRef.current.focus === LEAD_FOCUS_COMPOSE
                      ? initialComposeTemplateRef.current
                      : null
                  }
                  autoFocusComposer={focusTargetId === LEAD_SECTION_CONVERSATION_ID}
                />
              </Section>
            </div>
          )}
        </div>
      </div>

      {/* Reunión comercial + Villa en hold — fila full-width a media
          página cada una (no apiladas dentro de la columna izquierda).
          En mobile se apilan en este orden. Anclajes del hero
          (section-meeting / section-villa-hold) conservados. */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div id="section-meeting">
          <Section title="Reunión comercial" subtitle="Fecha de la reunión y seguimiento del dossier.">
            <LeadDossierCard lead={lead} onChanged={load} />
          </Section>
        </div>

        <div id="section-villa-hold">
          <Section title="Villa en hold" subtitle="Reserva temporal CRM (no afecta a la titularidad).">
            <LeadVillaHoldCard
              leadId={lead.lead_id}
              leadStatus={lead.status}
              onChanged={load}
            />
          </Section>
        </div>
      </div>

      {/* SECUNDARIO — herramientas complementarias, compactas, tras el
          grid operativo. Orden: Pasos del seguimiento → Mensajes
          sugeridos → Notas (auto-placement del grid conserva ese orden
          también en mobile). La sección standalone de Mensajes sugeridos
          se mantiene por los flujos no-WhatsApp / históricos; el composer
          de WhatsApp ya inserta sugerencias sin bajar hasta aquí. */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <CollapsibleSection
          title="Pasos del seguimiento"
          subtitle="Lista dinámica según la etapa actual."
          testid="section-checklist"
        >
          <div className="dv-card">
            <LeadChecklist lead={lead} onChanged={setLead} />
          </div>
        </CollapsibleSection>

        <div
          id={LEAD_SECTION_TEMPLATES_ID}
          className="scroll-mt-24"
          data-focused={focusTargetId === LEAD_SECTION_TEMPLATES_ID ? "true" : undefined}
        >
          <CollapsibleSection
            title="Mensajes sugeridos"
            subtitle="Plantillas para copiar, abrir en WhatsApp o marcar como enviadas."
            testid="section-templates"
            defaultOpen={templatesFocused}
          >
            <div className="dv-card">
              <LeadTemplates
                lead={lead}
                onChanged={setLead}
                preselectedTemplateId={initialComposeTemplateRef.current}
              />
            </div>
          </CollapsibleSection>
        </div>

        <Section title={`Notas (${notesThread.length})`} subtitle="Hilo cronológico de anotaciones.">
            <div className="dv-card space-y-4">
              <form onSubmit={addNote} className="flex gap-2 flex-wrap" data-testid="note-form">
                <select
                  value={newNoteKind}
                  onChange={(e) => setNewNoteKind(e.target.value)}
                  className="dv-input text-sm"
                  data-testid="note-kind"
                  disabled={busy}
                >
                  <option value="general">General</option>
                  <option value="call">Llamada</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="email">Email</option>
                  <option value="meeting">Reunión</option>
                  <option value="objection">Objeción</option>
                </select>
                <input
                  className="dv-input flex-1 min-w-[160px]"
                  placeholder="Añade una nota…"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  data-testid="note-input"
                  disabled={busy}
                />
                <button
                  type="submit"
                  className="dv-btn dv-btn-primary"
                  disabled={busy || !newNote.trim()}
                  data-testid="note-submit"
                >
                  <ChatCircleDots size={14} /> Guardar
                </button>
              </form>
              {notesThread.length === 0 ? (
                <div className="text-sm text-stone-400 text-center py-6">Sin notas aún.</div>
              ) : (
                <div className="space-y-3">
                  {notesThread.map(n => (
                    <div key={n.note_id || n.at} className="border-l-2 border-stone-300 pl-3" data-testid={`note-${n.note_id}`}>
                      {n.kind && n.kind !== "general" && (
                        <span className="inline-flex text-[10px] px-1.5 py-0.5 rounded-full border bg-stone-50 text-stone-600 border-stone-200 uppercase tracking-wider mb-1">
                          {n.kind}
                        </span>
                      )}
                      <div className="text-sm whitespace-pre-wrap">{n.text}</div>
                      <div className="text-xs text-stone-400 mt-1">
                        {n.author_name || "—"} · {fmtDate(n.at)}
                        {n.edited_at && <span className="ml-1 italic">(editado {fmtDate(n.edited_at)})</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
        </Section>
      </div>

      {/* Timeline — auditoría/historial, no trabajo diario. Fuera del grid
          operativo: ancho completo, plegado por defecto, al final. Datos y
          componente sin cambios. */}
      <div className="mt-6">
        <CollapsibleSection
          title="Timeline"
          subtitle="Historial de actividad y cambios del lead."
          testid="section-timeline"
        >
          <div className="dv-card">
            <LeadTimeline leadId={lead.lead_id} max={200} />
          </div>
        </CollapsibleSection>
      </div>

      {showConvert && (
        <ConvertToInvestorModal
          lead={lead}
          villas={villas}
          onClose={() => setShowConvert(false)}
          onConverted={async () => {
            setShowConvert(false);
            await load();
          }}
        />
      )}
    </AdminShell>
  );
}

/** ===================== Convert Modal ===================== */
function randomPassword() {
  // 12 chars: mix upper, lower, digit, symbol, starts with letter
  const lo = "abcdefghjkmnpqrstuvwxyz";
  const up = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const di = "23456789";
  const sy = "!@#$%&*";
  const all = lo + up + di + sy;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  const rest = Array.from({ length: 8 }, () => pick(all)).join("");
  return pick(up) + pick(lo) + pick(di) + pick(sy) + rest;
}

function ConvertToInvestorModal({ lead, villas, onClose, onConverted }) {
  const [form, setForm] = useState({
    name: lead.name || "",
    email: lead.email || "",
    phone: lead.phone || "",
    country: lead.country || "",
    villa_id: lead.converted_villa_id || "",
    password: randomPassword(),
    reserve_villa: false,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const regen = () => {
    setField("password", randomPassword());
    setCopied(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(form.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post(`/leads/${lead.lead_id}/convert`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        country: form.country || null,
        villa_id: form.villa_id || null,
        password: form.password,
        reserve_villa: !!form.reserve_villa,
      });
      await onConverted();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const selectableVillas = villas.filter(v => v.status !== "sold");

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 fade-up" data-testid="convert-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="p-6 border-b border-stone-200 flex items-start justify-between">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-stone-500 mb-1">Conversión comercial</div>
            <h2 className="font-serif-dv text-3xl">Convertir a inversor</h2>
            <p className="text-sm text-stone-500 mt-1">Crea una cuenta con rol Inversor y marca el lead como convertido.</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-900" data-testid="convert-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          {/* Identidad */}
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Datos del inversor</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-stone-500">Nombre *</label>
                <input required className="dv-input" value={form.name} onChange={e => setField("name", e.target.value)} data-testid="convert-name" />
              </div>
              <div>
                <label className="text-xs text-stone-500">Email *</label>
                <input required type="email" className="dv-input" value={form.email} onChange={e => setField("email", e.target.value)} data-testid="convert-email" />
              </div>
              <div>
                <label className="text-xs text-stone-500">Teléfono</label>
                <input className="dv-input" value={form.phone} onChange={e => setField("phone", e.target.value)} data-testid="convert-phone" />
              </div>
              <div>
                <label className="text-xs text-stone-500">País</label>
                <input className="dv-input" value={form.country} onChange={e => setField("country", e.target.value)} data-testid="convert-country" />
              </div>
            </div>
          </div>

          {/* Contexto read-only */}
          <div className="bg-stone-50 border border-stone-200 rounded-md p-3 text-xs text-stone-600 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><span className="text-stone-400 uppercase tracking-widest mr-2">Origen</span>{lead.source || "—"}</div>
            <div><span className="text-stone-400 uppercase tracking-widest mr-2">Campaña</span>{lead.campaign || "—"}</div>
          </div>

          {/* Villa (opcional) */}
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1">
              <House size={12} /> Villa asociada (opcional)
            </div>
            <select
              className="dv-input"
              value={form.villa_id}
              onChange={e => setField("villa_id", e.target.value)}
              data-testid="convert-villa"
            >
              <option value="">— Sin villa —</option>
              {selectableVillas.map(v => (
                <option key={v.villa_id} value={v.villa_id}>
                  {v.name} · {v.status === "reserved" ? "Reservada" : "Disponible"} · €{Math.round(v.price_eur).toLocaleString()}
                </option>
              ))}
            </select>
            {form.villa_id && (
              <label className="flex items-center gap-2 mt-2 text-sm text-stone-600" data-testid="convert-reserve-wrap">
                <input
                  type="checkbox"
                  checked={form.reserve_villa}
                  onChange={e => setField("reserve_villa", e.target.checked)}
                  data-testid="convert-reserve"
                />
                Marcar la villa como <em>reservada</em> (no transfiere la titularidad).
              </label>
            )}
            <div className="text-xs text-stone-400 mt-1">
              La asignación final de propietario (<code>owner_user_id</code>) se gestiona después desde <code>/villas</code>.
            </div>
          </div>

          {/* Password */}
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Contraseña temporal</div>
            <div className="flex gap-2 items-stretch">
              <input
                readOnly
                className="dv-input font-mono text-sm flex-1"
                value={form.password}
                onFocus={e => e.target.select()}
                data-testid="convert-password"
              />
              <button type="button" onClick={copy} className="dv-btn dv-btn-ghost" data-testid="convert-copy">
                <Copy size={14} /> {copied ? "Copiado" : "Copiar"}
              </button>
              <button type="button" onClick={regen} className="dv-btn dv-btn-ghost" data-testid="convert-regen">
                <ArrowsClockwise size={14} /> Regenerar
              </button>
            </div>
            <div className="text-xs text-stone-400 mt-1">
              Compártela manualmente con el inversor por un canal seguro. No se envía email automático.
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="convert-error">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="dv-btn dv-btn-ghost" data-testid="convert-cancel">
              Cancelar
            </button>
            <button type="submit" disabled={busy} className="dv-btn dv-btn-primary flex-1" data-testid="convert-submit">
              {busy ? "Convirtiendo…" : "Confirmar conversión"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function metaSummary(action, meta) {
  if (action === "lead.status_changed") return `${meta.from || "—"} → ${meta.to}`;
  if (action === "lead.assigned") return meta.assignee_name ? `→ ${meta.assignee_name}` : "Sin responsable";
  if (action === "lead.created_note") return `"${meta.preview || ""}"`;
  if (action === "lead.next_action_updated") {
    const date = meta.next_action_date ? ` (${meta.next_action_date})` : "";
    return `${meta.next_action || "—"}${date}`;
  }
  return "";
}

/**
 * CollapsibleSection — same visual rhythm as `<Section>` but with a toggle
 * caret in the header and render-on-demand children. Used for secondary lead
 * sections that don't need to be visible by default (templates, preferences,
 * channels, follow-up checklist).
 *
 * `summary` is an optional ReactNode rendered next to the title only when the
 * section is collapsed — small contextual chip so the user knows what's in
 * there without opening it.
 */
function CollapsibleSection({
  title, subtitle, defaultOpen = false, summary = null, testid, children,
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const headerId = testid ? `${testid}-header` : undefined;
  const panelId = testid ? `${testid}-panel` : undefined;
  return (
    <section className="mb-10" data-testid={testid}>
      <button
        type="button"
        id={headerId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-end justify-between mb-5 gap-4 flex-wrap text-left group"
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        <div className="min-w-0">
          <h2 className="font-serif-dv text-3xl leading-tight inline-flex items-center gap-2">
            {title}
            <span className="text-stone-400 group-hover:text-stone-700 transition">
              {open
                ? <CaretDown size={18} weight="bold" />
                : <CaretRight size={18} weight="bold" />}
            </span>
          </h2>
          {subtitle && <p className="text-stone-500 mt-1">{subtitle}</p>}
        </div>
        {!open && summary && (
          <span
            className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border bg-stone-50 text-stone-700 border-stone-200"
            data-testid={testid ? `${testid}-summary` : undefined}
          >
            {summary}
          </span>
        )}
      </button>
      {open && (
        <div id={panelId} role="region" aria-labelledby={headerId}>
          {children}
        </div>
      )}
    </section>
  );
}
